
import { SignInUrlBuilder } from '@tableau/shared-urls';
import { ButtonType, ButtonWidget } from '@tableau/widgets';

import { Messages } from '~src/codegen/Localize';
import { AuthenticationType } from '~src/settings/types/IServerSettingsUnauthenticated';
import { LoginErrorCodes } from '~src/ts/helpers/ErrorCodes';

import { AutoLogin } from './AutoLogin';
import { DisplayWindowsCredentialsLink } from './DisplayWindowsCredentialsLink';
import { LoginErrorMessage } from './LoginErrorMessage';
import { LoginPage } from './LoginPage';
import { RootLoginUiProps } from './RootLoginUi';
import { UsernameAndPasswordLogin } from './UsernameAndPasswordLogin';
import React = require('react');
import { LoginPageStyles } from '~src/authentication/styles/LoginPageStyles';

export interface AutoLoginProps extends RootLoginUiProps {
    serverAuthType: AuthenticationType;
}

function didAutoLoginFailInitially(signinQueryParams: SignInUrlBuilder.QueryParams): boolean {
    const autoLoginFailureCodes = [LoginErrorCodes.KerberosFailed, LoginErrorCodes.SspiFailed];
    return autoLoginFailureCodes.includes(signinQueryParams.error);
}

export function AutoLoginPage(props: AutoLoginProps) {
    const [errorCode, setErrorCode] = React.useState(props.signinQueryParams.error ?? 0);
    const [autoLoginDidFail, setAutoLoginDidFail] = React.useState(didAutoLoginFailInitially(props.signinQueryParams));
    const [shouldTryAutoLogin, setShouldTryAutoLogin] = React.useState(!props.signinQueryParams.disableAutoSignin);

    if (shouldTryAutoLogin) {
        return <AutoLogin {...props}
            serverAuthType={props.serverAuthType}
            onFailedAutoLogin={(errorCode: number) => {
                setErrorCode(errorCode);
                setAutoLoginDidFail(true);
                setShouldTryAutoLogin(false);
            }} />;
    } else {
        switch (props.serverAuthType) {
            case AuthenticationType.ClientCertificateWithFallback:
                return <UsernameAndPasswordLogin {...props} currentErrorCode={errorCode} />;

            case AuthenticationType.ClientCertificate:
                // AuthenticationType.ClientCertificate does not support fallback 
                // and it will show the login page with a try again button.
                return <LoginPage>
                    <LoginErrorMessage errorCode={errorCode} />
                    <ButtonWidget buttonType={ButtonType.Primary}
                        className={LoginPageStyles.submitButtonStyle}
                        testId='button-signin'
                        onClick={() => setShouldTryAutoLogin(true)}>
                        {Messages.signIn()}
                    </ButtonWidget>
                </LoginPage>;

            case AuthenticationType.Kerberos:
            case AuthenticationType.Sspi:
                return <UsernameAndPasswordLogin {...props} currentErrorCode={errorCode}>
                    <DisplayWindowsCredentialsLink
                        autoLoginDidFail={autoLoginDidFail}
                        retryAutoLogin={() => setShouldTryAutoLogin(true)} />
                </UsernameAndPasswordLogin>;

            default:
                throw new Error('Invalid auth type');
        }
    }
}
